import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Pages
import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import SectionPage from './pages/SectionPage';
import GuestBookPage from './pages/GuestBookPage';
import SearchPage from './pages/SearchPage';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCategories from './pages/admin/AdminCategories';
import AdminSections from './pages/admin/AdminSections';
import AdminGuestBook from './pages/admin/AdminGuestBook';
import AdminSettings from './pages/admin/AdminSettings';
import AdminQRCodes from './pages/admin/AdminQRCodes';
import AdminQuickInfo from './pages/admin/AdminQuickInfo';

// Components
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Context
import { AuthProvider } from './context/AuthContext';
import { SiteAuthProvider, useSiteAuth } from './context/SiteAuthContext';
import SitePasswordPrompt from './components/SitePasswordPrompt';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppContent() {
  const { isAuthenticated, isChecking } = useSiteAuth();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const isAdminRoute = currentPath.startsWith('/admin');

  if (!isAdminRoute && isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdminRoute && !isAuthenticated && !isChecking) {
    return <SitePasswordPrompt />;
  }

  return (
    <Router>
      <div className="App min-h-screen bg-snow-white">
        <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="category/:slug" element={<CategoryPage />} />
                <Route path="section/:slug" element={<SectionPage />} />
                <Route path="guestbook" element={<GuestBookPage />} />
                <Route path="search" element={<SearchPage />} />
              </Route>

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="sections" element={<AdminSections />} />
                <Route path="guestbook" element={<AdminGuestBook />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="quick-info" element={<AdminQuickInfo />} />
                <Route path="qr-codes" element={<AdminQRCodes />} />
              </Route>
            </Routes>
            <Toaster 
              position="top-center"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#2C1810',
                  color: '#fff',
                },
              }}
            />
          </div>
        </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SiteAuthProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </SiteAuthProvider>
    </QueryClientProvider>
  );
}

export default App;